/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import React from 'react';
import { css } from 'aphrodite';
import { Markup } from '@blend/consumer-components';

import { ActionButton, ProgressBar } from '@blend/consumer-components';

import { BlIcon } from '@blend/shared-react';
import FileUploader from '../FileUploader';
import { UploadItem } from '../UploadItem/UploadItem';
import { useConsumerTheme } from '../../../../../hooks/useConsumerTheme';
import { ModalProps, ModalState, UploadResponse, WithUploads } from '../types';
import styles from './style';
import { AlertBox } from '@components/AlertBox/AlertBox';
import { DocUploadIcon } from '@components/DocUploadIcon/DocUploadIcon';

export interface DesktopModalProps extends WithUploads, ModalProps, ModalState {
  canSubmit: boolean;
  multiple?: boolean;
  disabled: boolean;
  imageName?: string;
  uploadFile: (file: File) => Promise<UploadResponse>;
}

export const DesktopModal: React.FC<DesktopModalProps> = ({ ...props }) => {
  const {
    appConfig: { colors },
  } = useConsumerTheme();

  const renderHeader = () => {
    const { title, imageName } = props;
    return (
      <header className={css(styles.header)}>
        <span className={css(styles.titleContainer)}>
          {imageName && (
            <div className={css(styles.iconWrapper)}>
              <DocUploadIcon imageName={imageName} size="small" />
            </div>
          )}
          <h1 id="js-modal-title" data-exclude-fullstory="always" tabIndex={-1} className={css(styles.title)}>
            {title}
          </h1>
        </span>
      </header>
    );
  };

  const renderBody = () => {
    const { message, errorMessage, progress } = props;
    return (
      <div className={css(styles.body)}>
        <div className={css(styles.mainContent)}>
          <Markup element="p" className={css(styles.description)} data-exclude-fullstory="always" content={message} />
          {!!errorMessage && (
            <AlertBox type="error" icon="error">
              {errorMessage}
            </AlertBox>
          )}
          {renderUploadList()}
          {renderUploadButton()}
        </div>
        {!!progress && (
          <div className={css(styles.submitProgress)}>
            <ProgressBar
              description="Submitting documents"
              backgroundType="none"
              value={progress}
              actionColor={colors.action}
            />
          </div>
        )}
      </div>
    );
  };

  const renderUploadList = () => {
    const { disabled, fileIds, uploads, isSubmitting, onRemove } = props;
    const isUploadDisabled = isSubmitting || disabled;
    return (
      fileIds.length > 0 && (
        <ul className={css(styles.uploadList)}>
          {fileIds.map(fileId => (
            <li key={fileId} className={css(styles.uploadItem)}>
              {uploads[fileId] && (
                <UploadItem
                  disabled={isUploadDisabled}
                  upload={uploads[fileId]}
                  completed={!uploads[fileId].errorMessage}
                  onRemove={() => onRemove(fileId, uploads[fileId])}
                />
              )}
            </li>
          ))}
        </ul>
      )
    );
  };

  const renderUploadButton = () => {
    const { disabled, isSubmitting, fileIds, uploadFile, onUpload, multiple = true } = props;
    const isUploadDisabled = isSubmitting || disabled;
    return (
      <div className={css(styles.addButtonWrapper)}>
        <FileUploader
          id="DocUploaderModal-input"
          multiple={multiple}
          disabled={isUploadDisabled}
          uploadFile={uploadFile}
          onUploadChange={onUpload}
          customAllowedFileTypes={['pdf']}
        >
          <span className={css(styles.addButton)}>
            <BlIcon icon="plus" />
            <span className={css(styles.addAnotherText)}>Add {!!fileIds?.length ? ' another' : ''} file</span>
          </span>
        </FileUploader>
      </div>
    );
  };

  const renderFooter = () => {
    const { disabled, onSubmit, canSubmit, onCancel } = props;
    const isSubmitDisabled = !canSubmit || disabled;
    return (
      <footer className={css(styles.footer)}>
        <div className={css(styles.secondaryButton)}>
          <ActionButton
            data-dd-privacy="allow"
            option="secondary"
            sizing="md"
            onClick={onCancel}
            color={colors.action}
            font="UntitledSans"
          >
            Cancel
          </ActionButton>
        </div>
        <div className={css(styles.primaryButton)}>
          <ActionButton
            data-dd-privacy="allow"
            type="submit"
            option="primary"
            sizing="md"
            onClick={onSubmit}
            disabled={isSubmitDisabled}
            data-it="DocUploaderModal-submit"
            color={colors.action}
            font="UntitledSans"
          >
            I'm done
          </ActionButton>
        </div>
      </footer>
    );
  };

  return (
    <section className="it-DocUploaderModal">
      {renderHeader()}
      {renderBody()}
      {renderFooter()}
    </section>
  );
};
